import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type UIEvent } from 'react';
import { useReducedMotion } from '../../lib/useReducedMotion';
import type { SlideRead } from '../../types/api';
import { slidePictureTag } from '../generation/pictureStyles';
import { usePictureStyles } from '../generation/usePictureStyles';
import { SlideImage } from './SlideImage';
import { slideRoleTag } from './slideRole';

interface SlideStripProps {
  /** Ordered by `slide_index`, as the API returns them. */
  slides: SlideRead[];
  currentIndex: number;
  onSelect: (index: number) => void;
  /** A render has been outstanding a while. Wording only. */
  isSlow?: boolean;
}

/** A swipe has settled when the track has been still this long. */
const SCROLL_SETTLE_MS = 120;

/**
 * What the user will post: one slide large, the whole deck as a strip underneath.
 *
 * The large slide sits in a horizontal track with every slide of the deck, one
 * per snap point, so under a finger the deck swipes 1:1 with the platform's own
 * momentum and rubber-banding, and a trackpad pans it. Navigation is swipe,
 * click, arrow key or the two step controls — all of them move the same index,
 * which the parent owns so the layer editor and the findings panel stay pointed
 * at the slide on screen. A swipe reports its slide once the track has settled;
 * a change from the parent scrolls the track to match (smoothly, unless the
 * user asked for less motion).
 *
 * A run that rendered one picture — a generated single — gets the stage and
 * nothing else: steps, counter and thumbnails are all answers to "which of
 * these", and there is no which.
 *
 * Under the stage, the slide on screen names its picture in a small muted tag:
 * "{style} · {shot}" for a generated picture (and " · {treatment}" when it is
 * not full bleed), "Icon · {name}", "Type only" or "Your photo" by where the
 * picture came from, with the design agent's own words for the look as the
 * tooltip. Slides that carry none of it (packs, community designs, older
 * runs) show no tag.
 */
export function SlideStrip({ slides, currentIndex, onSelect, isSlow = false }: SlideStripProps) {
  const { pictureStyles } = usePictureStyles();
  const index = Math.min(Math.max(currentIndex, 0), slides.length - 1);
  const trackRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  // The index this component last painted, for listeners that outlive a render.
  const indexRef = useRef(index);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  // The parent's index is the truth: the track follows it. Instant on the first
  // paint (no flight across the deck on arrival) and on a resize (the snap
  // points moved under the scroll offset), smooth after a click or a key.
  const hasAlignedRef = useRef(false);
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const left = index * track.clientWidth;
    if (Math.abs(track.scrollLeft - left) < 1) return;
    const instant = !hasAlignedRef.current || reducedMotion;
    hasAlignedRef.current = true;
    track.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' });
  }, [index, reducedMotion]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    // Only a width change moves the snap points. A height change (a slide's
    // placeholder becoming its picture) must not yank the track mid-swipe.
    let lastWidth = track.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = track.clientWidth;
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;
      track.scrollTo({ left: indexRef.current * width, behavior: 'auto' });
    });
    observer.observe(track);
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (settleRef.current !== null) window.clearTimeout(settleRef.current);
    },
    [],
  );

  if (slides.length === 0) {
    return <p className="form__notice">Nothing made for this post yet.</p>;
  }

  const current = slides[index];
  if (!current) return null;

  const isDeck = slides.length > 1;
  // Read tolerantly: a server that predates the picture fields omits them.
  const pictureTag = slidePictureTag(
    {
      picture_style: current.picture_style ?? null,
      shot: current.shot ?? null,
      // Only a picture is treated: a colour or gradient slide ignores it (T2).
      picture_treatment:
        current.background.kind === 'asset' || current.background.kind === 'generated'
          ? current.picture_treatment
          : null,
      picture_source: current.picture_source ?? null,
      icon: current.icon ?? null,
    },
    pictureStyles,
  );
  const styleDirection = current.style_direction?.trim() || undefined;

  function step(delta: number) {
    const next = (index + delta + slides.length) % slides.length;
    onSelect(next);
  }

  /**
   * A swipe (or a trackpad pan) lands on whichever slide the snap chose. Read
   * once the track has been still a moment rather than on every scroll frame,
   * so the parent hears about the slide the user stopped on, not the ones they
   * passed. The track's own scroll after a parent change settles on the same
   * index and so reports nothing.
   */
  function handleScroll(event: UIEvent<HTMLDivElement>) {
    const track = event.currentTarget;
    if (settleRef.current !== null) window.clearTimeout(settleRef.current);
    settleRef.current = window.setTimeout(() => {
      settleRef.current = null;
      const width = track.clientWidth;
      if (width === 0) return;
      const landed = Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / width)));
      if (landed !== indexRef.current) onSelect(landed);
    }, SCROLL_SETTLE_MS);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    }
  }

  return (
    <div className="slide-strip" onKeyDown={handleKeyDown}>
      <div className="slide-strip__stage">
        {isDeck ? (
          <button
            className="button button--ghost slide-strip__step"
            type="button"
            aria-label="Previous slide"
            onClick={() => step(-1)}
          >
            ‹
          </button>
        ) : null}

        <div
          className="slide-strip__track"
          ref={trackRef}
          role="group"
          aria-label="Slides"
          onScroll={handleScroll}
        >
          {slides.map((slide) => (
            <div className="slide-strip__slide" key={slide.id}>
              <SlideImage slide={slide} variant="stage" isSlow={isSlow} />
            </div>
          ))}
        </div>

        {isDeck ? (
          <button
            className="button button--ghost slide-strip__step"
            type="button"
            aria-label="Next slide"
            onClick={() => step(1)}
          >
            ›
          </button>
        ) : null}
      </div>

      {pictureTag ? (
        <p className="slide-strip__picture" title={styleDirection}>
          {pictureTag}
        </p>
      ) : null}
      {pictureTag && styleDirection ? (
        // The tooltip's words, for a screen nothing hovers on (CSS shows it
        // only under hover: none). Hidden from the tree otherwise.
        <p className="slide-strip__direction">{styleDirection}</p>
      ) : null}

      {isDeck ? (
        <>
          <p className="slide-strip__indicator" role="status" aria-live="polite">
            Slide {index + 1} of {slides.length}
          </p>

          <ul className="slide-strip__thumbs">
            {slides.map((slide, position) => {
              const roleTag = slideRoleTag(slide.role);
              return (
                <li key={slide.id}>
                  <button
                    className={
                      position === index ? 'slide-thumb slide-thumb--current' : 'slide-thumb'
                    }
                    type="button"
                    aria-current={position === index}
                    onClick={() => onSelect(position)}
                  >
                    <SlideImage slide={slide} variant="thumb" isSlow={isSlow} />
                    <span className="slide-thumb__index">{position + 1}</span>
                    {roleTag === null ? null : (
                      <span className="badge badge--neutral">{roleTag}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}
